import { submittalStatus, submittalType } from '@/types/enums';
import dayjs from 'dayjs';
import z from 'zod';

const toOptionalEnum = <T extends [string, ...string[]]>(values: T) =>
  z
    .union([z.enum(values), z.literal('')])
    .optional()
    .transform((v) => (v === '' ? undefined : v));

const optionalNullableId = z.preprocess(
  (value) => (value === '' || Number.isNaN(value) ? null : value),
  z.coerce.number().nullable().optional(),
);

export const post = {
  body: z.object({
    project: z.coerce.number().min(1, { error: 'Project is required' }),
    parent: optionalNullableId,
    number: z.string().trim().min(1, { error: 'Submittal # is required' }),
    revision: z.coerce.number().optional(),
    spec_section: z.string().optional(),
    package: z.string().optional(),
    title: z.string().nonempty({ error: 'Title is required' }),
    description: z.string().optional(),
    submittal_type: z
      .enum(submittalType.map((item) => item.value))
      .default('shop_drawing'),
    zone_level: z.string().nonempty({ error: 'Zone level is required' }),
    discipline: z.string().optional(),
    system: z.string().optional(),
    vendor: z.string().optional(),
    drawing_refs: z.string().optional(),
    status: z.enum(submittalStatus.map((item) => item.value)).default('draft'),
    final_response: z.string().optional(),
    submit_by: z.string().optional(),
    submitted_at: z
      .string()
      .optional()
      .refine((val) => val === '' || dayjs(val).isValid(), {
        error: 'Select a valid date.',
      }),
    due_at: z
      .string()
      .nonempty({ error: 'Due date is required' })
      .refine((val) => dayjs(val).isValid(), { error: 'Select a valid date.' }),
    returned_at: z
      .string()
      .optional()
      .refine((val) => val === '' || dayjs(val).isValid(), {
        error: 'Select a valid date.',
      }),
    ball_in_court: z.string().nullable().optional(),
    metadata: z.string().optional(),

    // bucket A
    specification_title: z.string().optional(),
    spec_ref: z.string().optional(),
    attn_of: z.string().optional(),

    // bucket B
    export_to_name: z.string().optional(),
    export_to_address: z.string().optional(),
    export_attention: z.string().optional(),
    export_items_shipped_via: toOptionalEnum([
      'us_mail',
      'overnight',
      'courier',
      'karas_delivered',
      'ground',
      'uploaded_procore',
      'email',
      'other',
    ]),
    export_these_items_submitted: toOptionalEnum([
      'for_reference_only',
      'for_information_only',
      'for_review_comment',
      'out_for_approval',
      'for_final_approval',
      'for_record_distribution',
    ]),
    export_items_shipped_other: z.string().optional(),
    export_remarks: z.string().optional(),
    export_copy_to: z.string().optional(),
  }),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string(),
    data: z.string(),
    errors: z.string(),
  }),
  params: z.never(),
  query: z.never(),
};
const postSchema = z.object(post);
export type Post = z.infer<typeof postSchema>;

const returnAttachmentItem = z.object({
  id: z.number(),
  rfi: z.number().nullable().optional(),
  submittal: z.number().nullable().optional(),
  filename: z.string().nullable().optional(),
  file_url: z.string().nullable().optional(),
  uploaded_by: z
    .object({
      id: z.number().optional(),
      display_name: z.string().optional().nullable(),
      name: z.string().optional().nullable(),
    })
    .nullable()
    .optional(),
  created_at: z.date(),
});

const get = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    data: z.array(
      z.object({
        id: z.number(),
        project: z.number(),
        parent: z.number().nullable().optional(),
        number: z.string(),
        revision: z.number(),
        spec_section: z.string().nullable().optional(),
        package: z.string().nullable().optional(),
        title: z.string(),
        description: z.string().nullable().optional(),
        submittal_type: z.enum(submittalType.map((item) => item.value)),
        zone_level: z.string().nullable().optional(),
        discipline: z.string().nullable().optional(),
        system: z.string().nullable().optional(),
        vendor: z.string().nullable().optional(),
        drawing_refs: z.string().nullable().optional(),
        status: z.enum(submittalStatus.map((item) => item.value)),
        final_response: z.string().nullable().optional(),
        submit_by: z.date().nullable().optional(),
        submitted_at: z.date().nullable().optional(),
        due_at: z.date().nullable().optional(),
        returned_at: z.date().nullable().optional(),
        ball_in_court: z.string().nullable().optional(),
        created_by: z.number().nullable().optional(),
        remarks: z.string().nullable().optional(),
        metadata: z.any().nullable().optional(),
        attachments: z.array(z.any()).optional(),
        created_at: z.date(),
        updated_at: z.date(),

        specification_title: z.string().nullable().optional(),
        spec_ref: z.string().nullable().optional(),
        attn_of: z.string().nullable().optional(),
        gc_subm_transm_no: z.string().nullable().optional(),
        gc_approval_code: z.string().nullable().optional(),
        new_subm_no: z.string().nullable().optional(),

        export_to_name: z.string().nullable().optional(),
        export_to_address: z.string().nullable().optional(),
        export_attention: z.string().nullable().optional(),
        export_items_shipped_via: z.string().nullable().optional(),
        export_items_shipped_other: z.string().nullable().optional(),
        export_these_items_submitted: z.string().nullable().optional(),
        export_remarks: z.string().nullable().optional(),
        export_copy_to: z.string().nullable().optional(),
      }),
    ),
  }),
  params: z.never(),
  query: z
    .object({
      project: z.coerce.number().optional(),
    })
    .optional(),
};
const getSchema = z.object(get);
export type Get = z.infer<typeof getSchema>;

export const patch = {
  // due_at is required when creating, but an existing submittal may predate it
  // (importers can leave it unset) and that must not block editing other fields.
  body: post.body.partial().extend({
    due_at: z
      .string()
      .optional()
      .refine((val) => val === '' || dayjs(val).isValid(), {
        error: 'Select a valid date.',
      }),
  }),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string(),
    data: z.string(),
    errors: z.string(),
  }),
  params: z.object({
    submittal_id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchSchema = z.object(patch);
export type Patch = z.infer<typeof patchSchema>;

export const getSubmittal = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string(),
    data: z.object({
      id: z.number(),
      project: z.number(),
      parent: z.number().nullable().optional(),
      number: z.string(),
      revision: z.number(),
      spec_section: z.string().nullable().optional(),
      package: z.string().nullable().optional(),
      title: z.string(),
      description: z.string().nullable().optional(),
      submittal_type: z.enum(submittalType.map((item) => item.value)),
      zone_level: z.string().nullable().optional(),
      discipline: z.string().nullable().optional(),
      system: z.string().nullable().optional(),
      vendor: z.string().nullable().optional(),
      drawing_refs: z.string().nullable().optional(),
      status: z.enum(submittalStatus.map((item) => item.value)),
      final_response: z.string().nullable().optional(),
      submit_by: z.date().nullable().optional(),
      submitted_at: z.date().nullable().optional(),
      due_at: z.date().nullable().optional(),
      returned_at: z.date().nullable().optional(),
      ball_in_court: z.string().nullable().optional(),
      created_by: z.number().nullable().optional(),
      remarks: z.string().nullable().optional(),
      metadata: z.any().nullable().optional(),

      specification_title: z.string().nullable().optional(),
      spec_ref: z.string().nullable().optional(),
      attn_of: z.string().nullable().optional(),
      gc_subm_transm_no: z.string().nullable().optional(),
      gc_approval_code: z.string().nullable().optional(),
      new_subm_no: z.string().nullable().optional(),

      export_to_name: z.string().nullable().optional(),
      export_to_address: z.string().nullable().optional(),
      export_attention: z.string().nullable().optional(),
      export_items_shipped_via: z.string().nullable().optional(),
      export_items_shipped_other: z.string().nullable().optional(),
      export_these_items_submitted: z.string().nullable().optional(),
      export_remarks: z.string().nullable().optional(),
      export_copy_to: z.string().nullable().optional(),

      attachments: z.array(
        z.object({
          id: z.number(),
          submittal: z.number(),
          filename: z.string(),
          file: z.string(),
          external_file_url: z.string().nullable(),
          uploaded_by: z.number().nullable().optional(),
          created_at: z.date(),
          presigned_url: z.url(),
        }),
      ),

      created_at: z.date(),
      updated_at: z.date(),
    }),
    errors: z.string(),
  }),
  params: z.object({
    submittal_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getSubmittalSchema = z.object(getSubmittal);
export type GetSubmittal = z.infer<typeof getSubmittalSchema>;

export const postAttachments = {
  body: z.array(
    z.object({
      filename: z.string().nonempty({ error: 'Filename is required' }),
      file: z.url().nonempty({ error: 'File url is required' }),
    }),
  ),
  response: z.array(
    z.object({
      filename: z.string(),
      file: z.url(),
    }),
  ),
  params: z.object({
    submittal_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postAttachmentsSchema = z.object(postAttachments);
export type PostAttachments = z.infer<typeof postAttachmentsSchema>;

export const deleteAttachment = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
  }),
  params: z.object({
    submittal_id: z.coerce.number(),
    attachment_id: z.coerce.number(),
  }),
  query: z.never(),
};
const deleteAttachmentSchema = z.object(deleteAttachment);
export type DeleteAttachment = z.infer<typeof deleteAttachmentSchema>;

export const postExportPdf = {
  body: z.object({
    to_name: z.string().optional().nullable(),
    to_address: z.string().optional().nullable(),
    attention: z.string().optional().nullable(),

    items_shipped_via: z
      .enum([
        'us_mail',
        'overnight',
        'courier',
        'karas_delivered',
        'ground',
        'uploaded_procore',
        'email',
        'other',
      ])
      .optional()
      .nullable(),

    items_shipped_other: z.string().optional().nullable(),

    these_items_submitted: z
      .enum([
        'for_reference_only',
        'for_information_only',
        'for_review_comment',
        'out_for_approval',
        'for_final_approval',
        'for_record_distribution',
      ])
      .optional()
      .nullable(),

    remarks: z.string().optional().nullable(),
    copy_to: z.array(z.string()).optional(),
  }),
  response: z.any(),
  params: z.object({
    submittal_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postExportPdfSchema = z.object(postExportPdf);
export type PostExportPdf = z.infer<typeof postExportPdfSchema>;

export const postExportLog = {
  body: z.object({
    rows: z.array(
      z.object({
        submittal_id: z.coerce.number(),
      }),
    ),
  }),
  response: z.any(),
  params: z.never(),
  query: z.never(),
};
const postExportLogSchema = z.object(postExportLog);
export type PostExportLog = z.infer<typeof postExportLogSchema>;

export const getSubmittalReturnAttachments = {
  body: z.never(),
  response: z.array(returnAttachmentItem),
  params: z.object({
    submittal_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getSubmittalReturnAttachmentsSchema = z.object(
  getSubmittalReturnAttachments,
);
export type GetSubmittalReturnAttachments = z.infer<
  typeof getSubmittalReturnAttachmentsSchema
>;

export const postSubmittalReturnAttachment = {
  body: z.object({
    file: z.string().nonempty({ error: 'File is required' }),
    filename: z.string().optional().nullable(),
  }),
  response: returnAttachmentItem,
  params: z.object({
    submittal_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postSubmittalReturnAttachmentSchema = z.object(
  postSubmittalReturnAttachment,
);
export type PostSubmittalReturnAttachment = z.infer<
  typeof postSubmittalReturnAttachmentSchema
>;

export const deleteSubmittalReturnAttachment = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    return_id: z.coerce.number(),
    submittal_id: z.coerce.number(),
  }),
  query: z.never(),
};
const deleteSubmittalReturnAttachmentSchema = z.object(
  deleteSubmittalReturnAttachment,
);
export type DeleteSubmittalReturnAttachment = z.infer<
  typeof deleteSubmittalReturnAttachmentSchema
>;
